import { useId, useState } from "react";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import type { Company } from "@/api/queries";
import { useCompanyOptions } from "@/api/queries";
import { FormField } from "@/components/ui";
import { useSettled } from "@/lib/useSettled";

/**
 * *Empresa* on *Agregar seguimiento*: type, see the matches, pick one.
 *
 * It replaces a search box that silently refilled a closed `<select>` below
 * it — the search worked and nothing on screen said so, and the select only
 * ever held the first 50 of 25,901 companies. A combobox shows its matches
 * where the typing happens.
 */

/** A company as the form holds it: enough to send, and to show. */
export type PickedCompany = { id: number; label: string };

/** Two letters narrow the book to something a list can show; one does not. */
const MIN_LETTERS = 2;
/** What fits under the field without pushing the rest of the form away. */
const SHOWN = 8;

/** `name` when there is one, else `legal_name`; legacy rows carry stray spaces. */
export function companyLabel(company: Pick<Company, "name" | "legal_name">): string {
  return (company.name || company.legal_name).trim();
}

function folded(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/**
 * Names that start with what was typed come first; otherwise the server's own
 * order stands — the seller's accounts, then a colleague's, then nobody's.
 */
function ranked(results: Company[], term: string): Company[] {
  const wanted = folded(term);
  const rank = (company: Company) =>
    folded(companyLabel(company)).startsWith(wanted) ? 0 : 1;
  return [...results].sort((a, b) => rank(a) - rank(b));
}

export function CompanyPicker({
  value,
  onChange,
  canWorkOnOthers,
}: {
  value: PickedCompany | null;
  onChange: (company: PickedCompany | null) => void;
  /**
   * A manager may log on any account; a seller only on their own or an
   * unassigned one. The server refuses the rest either way (`may_work_on`).
   */
  canWorkOnOthers: boolean;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [text, setText] = useState(value?.label ?? "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const term = text.trim();
  const searching = value === null && term.length >= MIN_LETTERS;
  const settled = useSettled(searching ? term : "");
  const companies = useCompanyOptions(settled);
  const results = settled ? ranked(companies.data?.results ?? [], settled) : [];
  const shown = results.slice(0, SHOWN);
  const expanded = open && searching;
  const stale = settled !== term || companies.isPending || companies.isPlaceholderData;
  const activeCompany = shown[active];

  const workable = (company: Company) =>
    canWorkOnOthers || company.assignment_rank !== 2;

  function pick(company: Company) {
    if (!workable(company)) return;
    const label = companyLabel(company);
    setText(label);
    setOpen(false);
    onChange({ id: company.id, label });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!expanded) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (shown.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((index) => (index + step + shown.length) % shown.length);
    } else if (event.key === "Enter") {
      // Enter inside a form would otherwise be read as "save".
      event.preventDefault();
      if (activeCompany) pick(activeCompany);
    } else if (event.key === "Escape") {
      // Close the list, not the modal around it.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  }

  function ownership(company: Company): string {
    if (company.assignment_rank === 1) return t("newFollowUp.companyMine");
    if (company.assignment_rank === 3) return t("newFollowUp.companyUnassigned");
    const agent = company.current_agent_name ?? "—";
    return workable(company)
      ? t("newFollowUp.companyTheirs", { agent })
      : t("newFollowUp.companyTheirsLocked", { agent });
  }

  const status = !expanded
    ? null
    : stale && shown.length === 0
      ? t("newFollowUp.companySearching")
      : companies.isError
        ? t("newFollowUp.companySearchFailed")
        : shown.length === 0
          ? t("newFollowUp.companyNoMatches", { term })
          : null;
  const total = companies.data?.count ?? 0;

  return (
    <FormField
      label={t("newFollowUp.company")}
      hint={value ? undefined : t("newFollowUp.companyHint")}
    >
      {(id) => (
        <div>
          <div className="flex items-center gap-2">
            <input
              id={id}
              type="text"
              role="combobox"
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={expanded}
              {...(expanded ? { "aria-controls": listId } : {})}
              {...(expanded && activeCompany
                ? { "aria-activedescendant": `${listId}-${activeCompany.id}` }
                : {})}
              className="input min-w-0 flex-1"
              placeholder={t("newFollowUp.companyPlaceholder")}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setActive(0);
                setOpen(true);
                // Typing over a chosen company un-chooses it: what the box
                // says and what gets saved must never disagree.
                if (value) onChange(null);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              onKeyDown={onKeyDown}
            />
            {value ? (
              <button
                type="button"
                className="btn btn-ghost px-2 py-1 text-xs"
                aria-label={t("newFollowUp.companyClearLabel")}
                onClick={() => {
                  setText("");
                  onChange(null);
                }}
              >
                {t("newFollowUp.companyClear")}
              </button>
            ) : null}
          </div>

          {expanded ? (
            <div className="mt-1 border-2 border-rule bg-ground">
              {status ? (
                <p className="px-3 py-2 text-xs text-mute-600" aria-live="polite">
                  {status}
                </p>
              ) : null}
              {shown.length > 0 ? (
                <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto">
                  {shown.map((company, index) => {
                    const allowed = workable(company);
                    const label = companyLabel(company);
                    const details = [
                      company.name && company.legal_name.trim() !== label
                        ? company.legal_name.trim()
                        : "",
                      company.city ?? "",
                      ownership(company),
                    ].filter(Boolean);
                    return (
                      <li
                        key={company.id}
                        id={`${listId}-${company.id}`}
                        role="option"
                        aria-selected={index === active}
                        aria-disabled={!allowed}
                        className={`border-b border-rule px-3 py-2 last:border-0 ${
                          index === active ? "bg-panel" : ""
                        } ${allowed ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                        // Keep the focus in the box, or its blur closes the
                        // list before the click can land on the option.
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => pick(company)}
                      >
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-xs text-mute-600">{details.join(" · ")}</p>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {total > shown.length && !stale ? (
                <p className="border-t border-rule px-3 py-1.5 text-[11px] text-mute-600">
                  {t("newFollowUp.companyMore", {
                    shown: shown.length,
                    count: total.toLocaleString("es-MX"),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </FormField>
  );
}
